import type {
  BulkImportCITypeStat,
  BulkImportConflictPolicy,
  BulkImportIssue,
  BulkImportJob,
} from '@/entities/srm/ci-bulk-import';
import {
  Page,
  PaginationParamsRaw,
  serializePaginationParams,
} from '@/shared/api/pagination';
import { type AxiosRequestConfig } from 'axios';
import { srmInstance } from '@/shared/api/api-instances/srm/api-instance';

const baseURL = '/api/srm/v4/';
const defaultOptions = { baseURL };

/**
 * Реестр заданий и отчёт по строкам отдаются общей пагинацией сервиса: тело ответа -
 * это `{ items, total, page, size, ... }`, а не голый список, и порция задаётся
 * page/size, а не limit/offset. Отбор и сортировка - тоже общие параметры
 * `filter` и `sort`.
 */
export class CIBulkImportApi {
  /**
   * Файл уходит сырым телом запроса, а не через FormData: на маршруте импорта
   * выключена буферизация, и сервис разбирает поток по мере поступления.
   */
  static importCSV(
    ci_type_name: string,
    file: File,
    conflict_policy: BulkImportConflictPolicy,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post<BulkImportJob>(
      `/import/cis/bulk/csv/${ci_type_name}`,
      file,
      {
        ...defaultOptions,
        ...options,
        headers: { 'Content-Type': 'text/csv' },
        params: { source_name: file.name, conflict_policy },
      },
    );
  }

  static importJSON(
    file: File,
    conflict_policy: BulkImportConflictPolicy,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post<BulkImportJob>(`/import/cis/bulk/json`, file, {
      ...defaultOptions,
      ...options,
      headers: { 'Content-Type': 'application/json' },
      params: { source_name: file.name, conflict_policy },
    });
  }

  static getJobs(
    params: PaginationParamsRaw = {},
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.get<Page<BulkImportJob>>(`/import/cis/bulk/jobs`, {
      ...defaultOptions,
      ...options,
      params: serializePaginationParams(params),
    });
  }

  static getJob(job_id: string, options?: AxiosRequestConfig) {
    return srmInstance.get<BulkImportJob>(`/import/cis/bulk/jobs/${job_id}`, {
      ...defaultOptions,
      ...options,
    });
  }

  static getIssues(
    job_id: string,
    params: PaginationParamsRaw = {},
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.get<Page<BulkImportIssue>>(
      `/import/cis/bulk/jobs/${job_id}/issues`,
      {
        ...defaultOptions,
        ...options,
        params: serializePaginationParams(params),
      },
    );
  }

  /** Отчёт по строкам целиком, файлом CSV: имя файла приходит в Content-Disposition. */
  static exportIssues(job_id: string, options?: AxiosRequestConfig) {
    return srmInstance.get<Blob>(
      `/import/cis/bulk/jobs/${job_id}/issues/export`,
      {
        ...defaultOptions,
        ...options,
        responseType: 'blob',
      },
    );
  }

  /**
   * Результат по всем записям пакета целиком, файлом CSV: имя файла приходит в
   * Content-Disposition. Постранично результат читают внешние системы - в интерфейсе строка
   * на каждую запись пакета не нужна, человек читает отчёт по проблемным записям.
   */
  static exportResults(job_id: string, options?: AxiosRequestConfig) {
    return srmInstance.get<Blob>(
      `/import/cis/bulk/jobs/${job_id}/results/export`,
      {
        ...defaultOptions,
        ...options,
        responseType: 'blob',
      },
    );
  }

  /** Исходы записей пакета в разрезе ТКЕ: строка на каждый тип, встретившийся в файле. */
  static getCITypeStats(
    job_id: string,
    params: PaginationParamsRaw = {},
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.get<Page<BulkImportCITypeStat>>(
      `/import/cis/bulk/jobs/${job_id}/ci_types`,
      {
        ...defaultOptions,
        ...options,
        params: serializePaginationParams(params),
      },
    );
  }

  static cancelJob(job_id: string, options?: AxiosRequestConfig) {
    return srmInstance.post<BulkImportJob>(
      `/import/cis/bulk/jobs/${job_id}/cancel`,
      undefined,
      { ...defaultOptions, ...options },
    );
  }
}
